import React from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';

import { PhonePage, PrimaryButton, Spinner, TopBar } from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, MultiField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * Adding or editing a machine, on a phone.
 *
 * The side pane's form, one control per row. The stages it runs are chips
 * that toggle — the same picker the supervisor's screens use — rather than a
 * scroll box of 12px checkboxes.
 */

export default function PhoneMachineForm({
  mode, form, onChange, allStages, showPassword, setShowPassword, submitting, onSave, onCancel,
}) {
  const adding = mode === 'add';
  const set = (patch) => onChange((f) => ({ ...f, ...patch }));

  return (
    <PhonePage testID="machine-form-page">
      <TopBar
        eyebrow="MACHINE"
        title={adding ? 'Add machine' : `Edit ${mode.machine_name || 'machine'}`}
        subtitle={adding ? 'Operators sign in on the tablet with these credentials' : mode.username}
        onBack={onCancel}
        actions={(
          <button
            type="button"
            data-testid="save-machine-btn"
            onClick={onSave}
            disabled={submitting}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            {submitting ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : <><Check className="h-4 w-4" /> Save</>}
          </button>
        )}
      />

      <div className="flex-1 space-y-3 px-4 pb-10 pt-4">
        <Field label="Machine name *">
          <input
            data-testid="machine-name-input"
            className={INPUT_CLASS} style={inputStyle} placeholder="e.g. Komori-6C"
            value={form.machine_name}
            onChange={(e) => set({ machine_name: e.target.value })}
          />
        </Field>

        {adding && (
          <>
            <Field label="Username *">
              <input
                data-testid="machine-username-input"
                className={INPUT_CLASS} style={inputStyle} autoCapitalize="none" autoCorrect="off"
                value={form.username}
                onChange={(e) => set({ username: e.target.value })}
              />
            </Field>
            <Field label="Password *">
              <div className="relative">
                <input
                  data-testid="machine-password-input"
                  className={`${INPUT_CLASS} pr-11`}
                  style={inputStyle}
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => set({ password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center"
                  style={{ color: Colors.text.tertiary }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
          </>
        )}

        <Field label={`Runs which stages${form.linked_stage_ids.length ? ` (${form.linked_stage_ids.length} selected)` : ''}`}>
          {allStages.length === 0 ? (
            <p className="text-[13px]" style={{ color: Colors.text.tertiary }}>No stages available</p>
          ) : (
            <MultiField
              name="linked_stage_ids"
              value={form.linked_stage_ids}
              options={allStages.map((s) => ({ value: s.id, label: s.stage_name }))}
              onChange={(ids) => set({ linked_stage_ids: ids })}
            />
          )}
        </Field>

        <Field label="Notes">
          <textarea
            data-testid="machine-notes-input"
            className="min-h-[80px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
            style={inputStyle}
            rows={2}
            placeholder="Capacity, quirks, service dates…"
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Field>

        <PrimaryButton className="mt-5" onClick={onSave} busy={submitting} testID="save-machine-bottom">
          <Check className="h-4 w-4" /> {adding ? 'Create machine' : 'Save changes'}
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
